import { memo, useMemo } from 'react';
import { ui } from '@procuros/datachecks';

import { DataTableProvider } from 'components/DataTable/contexts/DataTableContext';
import { DisplayModes } from 'components/DataTable/Types';

import { useInterpolateVariables } from './contexts/SchemableFormVariablesContext/SchemableFormVariablesContext';
import { BooleanField } from './fields/BooleanField';
import { DateField } from './fields/DateField';
import { EnumerableField } from './fields/EnumerableField';
import { ExternalActionField } from './fields/ExternalActionField/ExternalActionField';
import { FileUploadField } from './fields/FileUploadField/FileUploadField';
import { LongTextField } from './fields/LongTextField';
import { MonetaryField } from './fields/MonetaryField';
import { MultiSelectField } from './fields/MultiSelectField';
import { NumberField } from './fields/NumberField';
import { PasswordField } from './fields/PasswordField';
import { RadioField } from './fields/RadioField';
import { TextArrayField } from './fields/TextArrayField';
import { TextField } from './fields/TextField';

type SchemableFormVariant = 'generic' | 'message';

export const SchemableForm = memo(
  ({
    dataPathPrefix,
    config,
    mode,
    isSubmitting,
    variant = 'message',
  }: {
    dataPathPrefix: string;
    config: ui.InfoSection;
    mode: DisplayModes;
    isSubmitting: boolean;
    variant?: SchemableFormVariant;
  }) => {
    if (!config.fields?.length) {
      return null;
    }

    return (
      <DataTableProvider name={dataPathPrefix}>
        {config.fields.map((field) => (
          <Field
            key={field.path}
            dataPathPrefix={dataPathPrefix}
            mode={mode}
            field={field}
            isSubmitting={isSubmitting}
            variant={variant}
          />
        ))}
      </DataTableProvider>
    );
  },
);

SchemableForm.displayName = 'SchemableForm';

const useInterpolatedField = <T extends ui.Field>(field: T): T => {
  const interpolate = useInterpolateVariables();

  return useMemo(
    () => ({
      ...field,
      ...(field.label !== undefined && { label: interpolate(field.label) }),
      ...(field.description !== undefined && { description: interpolate(field.description) }),
      ...(field.value_description !== undefined && { value_description: interpolate(field.value_description) }),
    }),
    [field, interpolate],
  );
};

const Field = ({
  dataPathPrefix,
  mode,
  field: rawField,
  isSubmitting,
  variant,
}: {
  dataPathPrefix: string;
  mode: DisplayModes;
  field: ui.Field;
  isSubmitting: boolean;
  variant: SchemableFormVariant;
}) => {
  const field = useInterpolatedField(rawField);
  const fieldId = `${dataPathPrefix}.${field.path}`;
  const inputVariant = variant === 'generic' ? 'large' : undefined;

  const readOnly = useMemo(() => {
    if (isSubmitting) {
      return true;
    }
    return Boolean(field.is_readonly) && mode !== DisplayModes.edit;
  }, [field.is_readonly, mode, isSubmitting]);

  switch (field.type) {
    case ui.FieldTypes.select:
      return (
        <EnumerableField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />
      );
    case ui.FieldTypes.boolean:
      return <BooleanField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />;

    case ui.FieldTypes.date:
    case ui.FieldTypes.date_time:
      return <DateField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />;

    case ui.FieldTypes.long_text:
      return <LongTextField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />;

    case ui.FieldTypes.monetary:
      return <MonetaryField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />;

    case ui.FieldTypes.number:
      return <NumberField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />;

    case ui.FieldTypes.password:
      return <PasswordField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />;

    case ui.FieldTypes.radio:
      return <RadioField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />;

    case ui.FieldTypes.text:
      return <TextField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />;

    case ui.FieldTypes.text_array:
      return (
        <TextArrayField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />
      );

    case ui.FieldTypes.multi_select:
      return (
        <MultiSelectField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />
      );

    case ui.FieldTypes.file_upload:
      return (
        <FileUploadField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />
      );

    case ui.FieldTypes.external_action:
      return (
        <ExternalActionField field={field} fieldId={fieldId} isReadOnly={readOnly} mode={mode} variant={inputVariant} />
      );
    default:
      return null;
  }
};
